import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import WellData from '../components/WellData';
import AttachmentManager from '../components/AttachmentManager';
import { apiFetch } from '../api/api';
import './BHA_v2.css';

// Stable configuration objects to prevent unnecessary re-renders
const BHA_ALLOWED_FILE_TYPES = ['image/*', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'];
const BHA_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const BHA_v2 = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Additional state for BHA-specific features
    const [selectedBha, setSelectedBha] = useState(null);
    const [wellId, setWellId] = useState(null);

    // Get wellId from URL parameters on component mount
    useEffect(() => {
        const urlWellId = searchParams.get('wellId');
        if (urlWellId) {
            setWellId(urlWellId);
        }
    }, [searchParams]);

    // Configuration for the WellData component (with dynamic attachment count)
    const wellDataConfig = {
        tables: { parent: 'BHA', child: 'BHA_Component' },
        identityColumns: { parent: 'BHAID', child: 'ComponentID' },
        matchKey: { parentKey: 'BHAID', childForeignKey: 'BHAID' },
        title: 'Well BHA',
        icon: '↘️',
        parentIcon: '🔧',
        childIcon: '⚙️',
        emptyParentMessage: 'No BHA records found for this well',
        emptyChildMessage: 'No components found for this BHA',
        // Display configuration
        showTabs: true,
        tabs: [
            { key: 'details', label: 'Components', icon: '⚙️' },
            { key: 'attachments', label: 'Attachments', icon: '📎' }
        ],
        // Custom field mappings
        parentDisplayFields: ['BHAName', 'HoleSize', 'DepthIn', 'DepthOut', 'TotalLength', 'TotalWeight'],
        // Column filters
        hiddenParentFields: ['WellID'],
        hiddenChildFields: ['BHAID'],
        // Additional features
        enableInlineEdit: true,
        enableImageUpload: true,
        enableAttachments: true
    };

    // Handle parent (BHA) selection
    const handleBhaSelect = useCallback((bha) => {
        console.log('🔧 BHA selected:', bha);
        setSelectedBha(bha);
    }, []);

    // Custom tab content renderer
    const renderCustomTabContent = useCallback((activeTab, selectedParent, childData, wellId) => {
        switch (activeTab) {
            case 'attachments':
                return (
                    <div>
                        {selectedParent && (
                            <div className="attachment-context-header" style={{
                                background: '#f8f9fa',
                                border: '1px solid #e9ecef',
                                borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '16px',
                                color: '#495057'
                            }}>
                                <p style={{ margin: 0, fontSize: '14px' }}>
                                    <strong>Attachment  : {selectedParent.BHAName || `BHA ${selectedParent.BHAID}`}</strong>
                                    {selectedParent.HoleSize && ` • Hole Size: ${selectedParent.HoleSize}"`}
                                    {selectedParent.DepthIn && ` • Depth: ${selectedParent.DepthIn}-${selectedParent.DepthOut}ft`}
                                </p>
                            </div>
                        )}
                        <AttachmentManager
                            parentRecord={selectedParent}
                            parentType="BHA"
                            parentIdField="BHAID"
                            allowedFileTypes={BHA_ALLOWED_FILE_TYPES}
                            maxFileSize={BHA_MAX_FILE_SIZE}
                            className="bha-attachments"
                        />
                    </div>
                );
            default:
                return null; // Let WellData handle the default 'details' tab
        }
    }, []);

    // Event handlers for WellData component
    const handleParentSave = useCallback((result) => {
        console.log('🔧 BHA saved:', result);
        // Add any BHA-specific post-save logic here
    }, []);

    const handleChildSave = useCallback((result) => {
        console.log('⚙️ Component saved:', result);
        // Add any component-specific post-save logic here
    }, []);

    const handleWellChange = useCallback((wellId) => {
        setSearchParams({ wellId });
    }, [setSearchParams]);

    return (
        <div className="bha-v2-container">
            <WellData
                key={wellId}
                config={wellDataConfig}
                wellId={wellId}
                onWellChange={handleWellChange}
                onParentSelect={handleBhaSelect}
                onParentSave={handleParentSave}
                onChildSave={handleChildSave}
                renderCustomTabContent={renderCustomTabContent}
            />
        </div>
    );
};

export default BHA_v2; 